import type { ModulesLoader } from '@wix/ssr-modules-loader'
import type {
	ISiteAssetsComponentsRegistry,
	ISiteAssetsModel } from '@wix/editor-elements-registry/2.0/siteAssets'
import {
	registry as registrySA
} from '@wix/editor-elements-registry/2.0/siteAssets'
import type {
	IThunderboltPreviewComponentsRegistry,
	IThunderboltPreviewComponentModel } from '@wix/editor-elements-registry/2.0/preview'
import {
	registry as registryPreview
} from '@wix/editor-elements-registry/2.0/preview'
import type { ILibraryTopology, IRegistryParameters } from '@wix/editor-elements-registry/2.0/types'
import { createRegistryInstanceCache } from '@wix/editor-elements-registry/2.0/createRegistryInstanceCache'
import type { IRegistryRunAndReport } from './metrics'
import { getReportMetricName } from './metrics'
import { getLoggerClient } from './logger'
import type { ComponentsSkinParams } from '@wix/thunderbolt-becky-root'
import { getComponentSkinParamsByComponent, getSkinParamsBySimpleSkinName } from './common'

export { ILibraryTopology }
export type IComponentMappers = Record<string, ISiteAssetsModel['mapper']>
type ComponentsRegistry = IThunderboltPreviewComponentsRegistry | ISiteAssetsComponentsRegistry

export interface ICreateRegistrySiteAssetsParams {
	libraries: Array<ILibraryTopology>
	modulesLoader: ModulesLoader
	isExperimentOpen: IRegistryParameters['isExperimentOpen']
	isPlatformBuilderModule: boolean
	isEditor: boolean
}

interface ISiteAssetsModelWithMapper {
	mapper?: Record<string, any>
	previewMapper?: Record<string, any>
}

const hasMapper = (model: ISiteAssetsModelWithMapper) => {
	return !!model.mapper || !!model.previewMapper
}

export interface IComponentsRegistrySiteAssets {
	getRegistryAPI: () => ComponentsRegistry
	loadComponentMappers: () => Promise<IComponentMappers>
	loadSkinParams: () => Promise<ComponentsSkinParams>
}

const METRIC_CREATE_REGISTRY = getReportMetricName({ host: 'siteAssets' })
const METRIC_CREATE_REGISTRY_CACHED = getReportMetricName({ host: 'siteAssets', cached: true })

const cache = createRegistryInstanceCache<ComponentsRegistry>()

async function getRegistryAPI({
	libraries,
	modulesLoader,
	runAndReport,
	isExperimentOpen,
	isPlatformBuilderModule,
	isEditor,
}: ICreateRegistrySiteAssetsParams & IRegistryRunAndReport): Promise<ComponentsRegistry> {
	const registryParams: IRegistryParameters = {
		fetcher: (url: string) => {
			const content = (modulesLoader as ModulesLoader).absoluteUrl<string>(url, {
				shouldUseNodeRequire: false,
			})

			return content
		},
		mode: 'eager',
		libraries,
		isExperimentOpen,
		getSentryClient: getLoggerClient(isExperimentOpen),
	}
	if (isPlatformBuilderModule && isEditor) {
		return cache.getRegistryAPI({
			libraries,
			factory: () => {
				return runAndReport(METRIC_CREATE_REGISTRY, () => registryPreview(registryParams))
			},
		})
	} else {
		return cache.getRegistryAPI({
			libraries,
			factory: () => {
				return runAndReport(METRIC_CREATE_REGISTRY, () => registrySA(registryParams))
			},
		})
	}
}

export async function createComponentsRegistrySiteAssets(options: {
	libraries: Array<ILibraryTopology>
	modulesLoader: ModulesLoader
	runAndReport: <T>(metric: string, fn: () => Promise<T>) => Promise<T>
	isExperimentOpen: IRegistryParameters['isExperimentOpen']
	isPlatformBuilderModule: boolean
	isEditor: boolean
}): Promise<IComponentsRegistrySiteAssets> {
	const { runAndReport } = options

	const registryAPI = await runAndReport(METRIC_CREATE_REGISTRY_CACHED, () => getRegistryAPI(options))
	let modelsCache: Record<string, ISiteAssetsModel | IThunderboltPreviewComponentModel> | null

	const getModels = async () => {
		const models = modelsCache ?? (await registryAPI.loadAllComponents())
		if (!modelsCache) {
			modelsCache = models
		}
		return models
	}

	return {
		async loadComponentMappers() {
			const models = await getModels()

			return Object.keys(models)
				.filter((componentType) => hasMapper(models[componentType]))
				.reduce<IComponentMappers>((acc, componentType) => {
					const model = models[componentType] as ISiteAssetsModelWithMapper
					return {
						...acc,
						[componentType]: {
							...(model.mapper || {}),
							...(model.previewMapper || {}),
						},
					}
				}, {})
		},

		async loadSkinParams() {
			const models = await getModels()
			const componentsSkinParamsByComponent = getComponentSkinParamsByComponent(models)
			const skinParamsBySimpleSkinName = getSkinParamsBySimpleSkinName(componentsSkinParamsByComponent)
			return skinParamsBySimpleSkinName
		},

		getRegistryAPI() {
			return registryAPI
		},
	}
}
